import { NATIVE_PLATFORM } from '../config/runtime';

/**
 * "Take photo or choose a file?" for every upload in the Android app.
 *
 * Android's WebView offers the camera only to an <input type=file> marked
 * `capture`, and then offers nothing else; without it, the file picker only.
 * Every upload in the ERP — weighment slips, receipts, certificates, QC photos
 * — is an unmarked input, so on a phone the operator could not photograph the
 * paper in their hand. iOS already asks (Take Photo / Photo Library / Files),
 * so this runs on Android only.
 *
 * A tap on an image-accepting input is caught before the picker opens and
 * announced (FILE_SOURCE_EVENT); NativeShell shows the choice. Both answers
 * run inside the operator's tap on the sheet, which is what lets them open a
 * picker at all. A photo is handed to the original input exactly as if it had
 * been picked there, so the page's own onChange does the rest — no upload
 * screen was changed for this.
 */

export const FILE_SOURCE_EVENT = 'erp:file-source';

const passThrough = new WeakSet();
let cameraInput = null;
let installed = false;

function acceptsImages(accept) {
  const a = (accept || '').toLowerCase().trim();
  if (!a) return true;
  return a.split(',').some((t) => {
    const x = t.trim();
    return x === '*/*' || x.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.heic', '.webp'].includes(x);
  });
}

export function installCameraChooser() {
  if (installed || NATIVE_PLATFORM !== 'android') return;
  installed = true;
  document.addEventListener('click', (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'file') return;
    if (passThrough.has(input)) {
      passThrough.delete(input);
      return;
    }
    if (input === cameraInput || input.hasAttribute('capture') || !acceptsImages(input.accept)) return;
    e.preventDefault();
    window.dispatchEvent(new CustomEvent(FILE_SOURCE_EVENT, { detail: { input } }));
  }, true);
}

/** Call from the tap on "Take photo". */
export function takePhotoInto(input) {
  if (!cameraInput) {
    cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.setAttribute('capture', 'environment');
    cameraInput.style.display = 'none';
    document.body.appendChild(cameraInput);
  }
  cameraInput.value = '';
  cameraInput.onchange = () => {
    const file = cameraInput.files && cameraInput.files[0];
    if (!file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;
    // React's onChange for a file input is the native `change` event.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  cameraInput.click();
}

/** Call from the tap on "Choose file": the original picker, untouched. */
export function chooseFileInto(input) {
  passThrough.add(input);
  input.click();
}
